import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Recommendation, RecommendationGuidance, RejectionReason } from '@lizrd/contracts';
import {
  addRecommendationComment,
  getAiStatus,
  getGraphNeighbors,
  getRecommendationComments,
  getRecommendationGuidance,
  getRecommendationHistory,
  getRecommendations,
  getResourceHistory,
  guideRecommendation,
  listIntegrations,
  updateRecommendation,
} from '../lib/api';
import { useAuthStore } from '../store/auth';
import {
  PriorityControl,
  AssigneeControl,
} from '../components/recommendations/RecommendationControls';
import { Permission, roleHasPermissions } from '@lizrd/contracts';
import { Card } from '../components/ui/card';
import { GuardrailBanner } from '../components/recommendations/GuardrailBanner';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Spinner } from '../components/ui/spinner';
import { SkeletonText } from '../components/ui/skeleton';
import { LineChart } from '../components/ui/line-chart';
import { StepProgress } from '../components/ui/step-progress';
import type { StepState } from '../components/ui/stepper';
import { InlineMarkdown, MarkdownLite } from '../components/ui/markdown-lite';
import { useToast } from '../components/ui/toast';
import {
  RecommendationLifecycle,
  type LifecycleAction,
} from '../components/RecommendationLifecycle';
import { RejectRecommendationModal } from '../components/RejectRecommendationModal';
import { ProviderChip } from '../components/recommendations/ProviderChip';
import { EFFORT_META, relativeTime, typeLabel, usd } from '../lib/graph-visuals';
import { RECO_CATEGORY_META } from '../lib/reco-categories';
import { appliedAlternative } from '../lib/reco-compliance';
import { CategoryIcon } from '../components/CategoryIcon';
import { NodeTypeIcon } from '../components/node-icons';
import { FileText } from 'lucide-react';

const GUIDANCE_STAGES = [
  'Reading resource & code context',
  'Preparing the implementation steps',
  'Verifying the change',
];

// Guidance regenerates once every 15 minutes — mirrors the server cooldown and
// the modal, so the button counts down instead of letting a click 429.
const GUIDANCE_REGEN_COOLDOWN_MS = 15 * 60_000;

/** "Prepared … · Regenerate" footer for the guidance section — mirrors the modal:
 * the working guide stays on screen, the button counts down through the 15-min
 * cooldown, and a failed regenerate only toasts (never drops the guide). */
function RegenerateFooter({
  generatedAt,
  canManage,
  regenerating,
  onRegenerate,
}: {
  generatedAt: string;
  canManage: boolean;
  regenerating: boolean;
  onRegenerate: () => void;
}) {
  const remainingMs = GUIDANCE_REGEN_COOLDOWN_MS - (Date.now() - new Date(generatedAt).getTime());
  const inCooldown = remainingMs > 0;
  const mins = Math.ceil(remainingMs / 60_000);
  return (
    <div className="flex items-center justify-between gap-3 border-t border-hairline pt-2 text-[0.65rem] text-muted">
      <span>Prepared {new Date(generatedAt).toLocaleString()}</span>
      {canManage && (
        <div className="flex items-center gap-2">
          {inCooldown && !regenerating && <span>Available again in {mins} min</span>}
          <button
            onClick={onRegenerate}
            disabled={regenerating || inCooldown}
            title={inCooldown ? `Regenerate available in ${mins} min` : undefined}
            className="font-semibold text-iris hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline"
          >
            {regenerating
              ? '↻ Regenerating…'
              : inCooldown
                ? `↻ Regenerate in ${mins}m`
                : '↻ Regenerate'}
          </button>
        </div>
      )}
    </div>
  );
}

/** Lifecycle steps for the header progress (rejected/superseded shown separately). */
function stepperSteps(status: Recommendation['status']): { label: string; state: StepState }[] {
  const order = ['Open', 'Accepted', 'Executed', 'Validated'];
  const reached: Record<string, number> = {
    open: 0,
    accepted: 1,
    completed: 2,
    failed_validation: 2,
    validated: 3,
  };
  const at = reached[status] ?? 0;
  return order.map((label, i) => ({
    label,
    state: (i < at ? 'done' : i === at ? 'active' : 'pending') as StepState,
  }));
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="font-display text-sm font-semibold uppercase tracking-wide text-haze">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </Card>
  );
}

/** A labelled block of guidance text/steps. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 text-sm text-haze">{children}</div>
    </div>
  );
}

function DiffBlock({ diff }: { diff: string }) {
  return (
    <pre className="overflow-x-auto rounded-md border border-hairline bg-surface-2/60 p-3 text-xs leading-relaxed">
      {diff.split('\n').map((line, i) => {
        const tone = line.startsWith('+')
          ? 'text-success'
          : line.startsWith('-')
            ? 'text-danger'
            : 'text-haze';
        return (
          <div key={i} className={`whitespace-pre ${tone}`}>
            {line || ' '}
          </div>
        );
      })}
    </pre>
  );
}

/** Per-resource metric chart (utilization or cost over time). */
function MetricChart({
  label,
  points,
  color,
  format,
  yMax,
  yTicks,
}: {
  label: string;
  points: { day: string; value: number }[];
  color: string;
  format: (n: number) => string;
  yMax?: number;
  yTicks?: number[];
}) {
  return (
    <div>
      <div className="mb-1 text-xs text-haze">{label}</div>
      {points.length >= 2 ? (
        <LineChart
          height={150}
          series={[{ key: label, label, color, points }]}
          formatValue={format}
          yMax={yMax}
          yTicks={yTicks}
          formatTick={yTicks ? (n) => `${n}` : undefined}
        />
      ) : (
        <div className="grid h-[110px] place-items-center rounded-sm bg-surface-2/40 text-[0.7rem] text-muted">
          Collecting — appears after the next sync
        </div>
      )}
    </div>
  );
}

/**
 * Recommendation Details — the dedicated engineering workspace for one cost
 * optimization (DET-001..010). A linkable page that goes beyond the board card:
 * the deterministic evidence with the resource's own metric charts, the reveal-on-
 * demand implementation guidance, validation (estimated vs realized) over time,
 * the involved code↔cloud assets, and an activity + comment thread.
 */
export function RecommendationDetailPage() {
  const { domainId = '', recId = '' } = useParams();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role ? roleHasPermissions(role, [Permission.RecommendationsManage]) : false;
  const { toast } = useToast();
  const qc = useQueryClient();

  const recs = useQuery({
    queryKey: ['recommendations', domainId],
    queryFn: () => getRecommendations(domainId),
    refetchInterval: 60_000,
  });
  const rec = recs.data?.items.find((r) => r.id === recId) ?? null;

  const ai = useQuery({
    queryKey: ['ai-status', domainId],
    queryFn: () => getAiStatus(domainId),
  });
  // Upsell the (optional) code connection where it pays off: a recommendation
  // with no owning repo, in a domain that has no code base connected, can only
  // get prose guidance. Connecting code unlocks an exact diff (audit F1).
  const integrations = useQuery({
    queryKey: ['integrations', domainId],
    queryFn: () => listIntegrations(domainId),
  });
  const hasCodeBase = (integrations.data ?? []).some(
    (i) => i.kind === 'SCM' && i.status === 'CONNECTED',
  );
  const guidance = useQuery({
    queryKey: ['recommendation-guidance', domainId, recId],
    queryFn: () => getRecommendationGuidance(domainId, recId),
    enabled: !!rec,
  });
  const history = useQuery({
    queryKey: ['recommendation-history', domainId, recId],
    queryFn: () => getRecommendationHistory(domainId, recId),
    enabled: !!rec,
  });
  const comments = useQuery({
    queryKey: ['recommendation-comments', domainId, recId],
    queryFn: () => getRecommendationComments(domainId, recId),
    enabled: !!rec,
  });
  const resourceHistory = useQuery({
    queryKey: ['resource-history', domainId, rec?.nodeId],
    queryFn: () => getResourceHistory(domainId, rec!.nodeId!),
    enabled: !!rec?.nodeId,
  });
  const neighbors = useQuery({
    queryKey: ['graph-neighbors', domainId, rec?.nodeId],
    queryFn: () => getGraphNeighbors(domainId, rec!.nodeId!),
    enabled: !!rec?.nodeId,
  });

  // Reveal-on-demand guidance pipeline (DET-010).
  const [phase, setPhase] = useState(0);
  const [altApplied, setAltApplied] = useState(false);
  const generate = useMutation({
    mutationFn: (alternativeIntent?: string) =>
      guideRecommendation(domainId, recId, alternativeIntent),
    onSuccess: (g) => {
      qc.setQueryData<RecommendationGuidance>(['recommendation-guidance', domainId, recId], g);
      // Refresh the recommendation (carries complianceAlternativeApplied), the
      // modal's guidance cache, and the compliance page.
      qc.invalidateQueries({ queryKey: ['recommendations', domainId] });
      qc.invalidateQueries({ queryKey: ['guidance', domainId, recId] });
      qc.invalidateQueries({ queryKey: ['compliance', domainId] });
    },
    onError: (e) =>
      toast({
        title: e instanceof Error ? e.message : 'Could not generate guidance',
        tone: 'danger',
      }),
  });
  const generating = generate.isPending;
  useEffect(() => {
    if (!generating) return;
    setPhase(0);
    const t = setInterval(() => setPhase((p) => Math.min(p + 1, GUIDANCE_STAGES.length - 1)), 1100);
    return () => clearInterval(t);
  }, [generating]);

  // Lifecycle actions.
  const [rejecting, setRejecting] = useState(false);
  const act = useMutation({
    mutationFn: (v: {
      action: LifecycleAction;
      rejectionReason?: RejectionReason;
      rejectionNote?: string;
    }) =>
      updateRecommendation(domainId, recId, {
        action: v.action,
        rejectionReason: v.rejectionReason,
        rejectionNote: v.rejectionNote,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recommendations', domainId] });
      qc.invalidateQueries({ queryKey: ['recommendation-history', domainId, recId] });
    },
    onError: (e) =>
      toast({ title: e instanceof Error ? e.message : 'Action failed', tone: 'danger' }),
  });
  const onAction = (action: LifecycleAction) => {
    if (action === 'reject') setRejecting(true);
    else act.mutate({ action });
  };

  // Comment thread.
  const [comment, setComment] = useState('');
  const postComment = useMutation({
    mutationFn: () => addRecommendationComment(domainId, recId, comment.trim()),
    onSuccess: () => {
      setComment('');
      qc.invalidateQueries({ queryKey: ['recommendation-comments', domainId, recId] });
    },
  });

  const steps = useMemo(() => (rec ? stepperSteps(rec.status) : []), [rec]);

  if (recs.isLoading) {
    return (
      <div className="space-y-4">
        <SkeletonText lines={2} lineClassName="h-6" />
        <SkeletonText lines={6} />
      </div>
    );
  }
  if (!rec) {
    return (
      <Card className="space-y-3 p-6">
        <h1 className="font-display text-xl font-bold text-bone">Recommendation not found</h1>
        <p className="text-sm text-muted">
          It may have been superseded or removed on a recent sync.
        </p>
        <Link
          to={`/domains/${domainId}/cost-optimization`}
          className="text-sm text-iris hover:text-iris-strong"
        >
          ← Back to Optimizations
        </Link>
      </Card>
    );
  }

  const cat = RECO_CATEGORY_META[rec.category];
  const g = guidance.data;
  const showValidation = ['completed', 'validated', 'failed_validation'].includes(rec.status);
  // Once the compliant alternative is accepted (locally or persisted), the headline
  // saving reflects that smaller change rather than the original.
  const alt = appliedAlternative({
    complianceAlternativeApplied: !!rec.complianceAlternativeApplied || altApplied,
    complianceFlags: rec.complianceFlags,
  });

  return (
    <div className="space-y-5">
      <Link
        to={`/domains/${domainId}/cost-optimization`}
        className="text-xs text-muted transition-colors hover:text-bone"
      >
        ← Optimizations
      </Link>

      {/* Header */}
      <Card className="space-y-4 p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Badge variant="neutral">
                <CategoryIcon category={rec.category} className="h-3.5 w-3.5" /> {cat.label}
              </Badge>
            </div>
            <h1 className="mt-2 font-display text-2xl font-bold text-bone">
              <InlineMarkdown text={rec.title} />
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <ProviderChip provider={rec.provider} />
              <p className="text-sm text-muted">
                {rec.resourceName} · {typeLabel(rec.resourceType, rec.provider)}
              </p>
            </div>
          </div>
          <RecommendationLifecycle
            status={rec.status}
            pending={act.isPending}
            onAction={onAction}
            size="lg"
            readOnly={!canManage}
          />
        </div>

        {/* Metric chips */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-md border border-hairline bg-surface-2 p-3">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              {rec.status === 'validated'
                ? 'Realized / month'
                : alt
                  ? 'Est. saving / month · compliant alt.'
                  : 'Estimated saving / month'}
            </div>
            <div className="mt-0.5 font-display text-xl font-bold text-iris">
              {rec.status === 'validated'
                ? usd(rec.realizedMonthlySaving ?? 0)
                : usd(alt?.saving ?? rec.estimatedMonthlySaving)}
            </div>
            {rec.status === 'validated' ? (
              <div className="text-[0.65rem] text-muted">
                est. {usd(rec.estimatedMonthlySaving)}
              </div>
            ) : alt ? (
              <div className="text-[0.65rem] text-muted">
                {alt.basis ?? 'compliant alternative'}
              </div>
            ) : null}
          </div>
          <div className="rounded-md border border-hairline bg-surface-2 p-3">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              Effort
            </div>
            <div className="mt-0.5 text-sm text-bone">
              {EFFORT_META[rec.effortLevel]?.label ?? rec.effortLevel}
            </div>
            <div className="text-[0.65rem] text-muted">{EFFORT_META[rec.effortLevel]?.band}</div>
          </div>
          <div className="rounded-md border border-hairline bg-surface-2 p-3">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              Priority
            </div>
            <div className="mt-1">
              <PriorityControl rec={rec} envId={domainId} canManage={canManage} />
            </div>
          </div>
          <div className="rounded-md border border-hairline bg-surface-2 p-3">
            <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-muted">
              Confidence
            </div>
            <div className="mt-0.5 text-sm capitalize text-bone">{rec.savingConfidence}</div>
          </div>
        </div>

        {/* Lifecycle stepper */}
        {['rejected', 'superseded', 'dismissed'].includes(rec.status) ? (
          <p className="text-xs text-muted">
            This recommendation is <span className="text-bone">{rec.status}</span>.
          </p>
        ) : (
          <StepProgress steps={steps} spinActive={false} />
        )}
      </Card>

      <GuardrailBanner
        rec={rec}
        domainId={domainId}
        canManage={canManage}
        onAcceptAlternative={(intent) => {
          setPhase(0);
          generate.mutate(intent, { onSuccess: () => setAltApplied(true) });
        }}
        accepting={generate.isPending}
        applied={!!rec.complianceAlternativeApplied || altApplied}
      />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-5 lg:col-span-2">
          <Section title="Recommendation">
            <div className="space-y-4">
              <MarkdownLite text={rec.rationale} className="text-sm leading-relaxed text-haze" />
              <Field label="Recommended action">{rec.suggestedAction}</Field>
              <Field label="How the saving was derived">{rec.savingsBasis}</Field>
              <Field label="Why we're this confident">{rec.confidenceReason}</Field>
            </div>
          </Section>

          <Section title="Evidence & metrics">
            <div className="space-y-4">
              {rec.evidence.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {rec.evidence.map((e) => (
                    <span
                      key={e.label}
                      className="rounded-md border border-hairline bg-surface-2 px-2.5 py-1 text-xs"
                    >
                      <span className="text-muted">{e.label}: </span>
                      <span className="font-medium text-bone">{e.value}</span>
                    </span>
                  ))}
                </div>
              )}
              {rec.nodeId ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <MetricChart
                    label="CPU utilization"
                    points={resourceHistory.data?.cpuPct ?? []}
                    color="var(--iris)"
                    format={(n) => `${Math.round(n)}%`}
                    yMax={100}
                    yTicks={[0, 25, 50, 75, 100]}
                  />
                  <MetricChart
                    label="Memory utilization"
                    points={resourceHistory.data?.memPct ?? []}
                    color="var(--info)"
                    format={(n) => `${Math.round(n)}%`}
                    yMax={100}
                    yTicks={[0, 25, 50, 75, 100]}
                  />
                  <MetricChart
                    label="Monthly cost"
                    points={resourceHistory.data?.costMonthly ?? []}
                    color="var(--warning)"
                    format={usd}
                  />
                </div>
              ) : (
                <p className="text-xs text-muted">
                  No live metrics for this resource type — it&apos;s tracked by configuration/state.
                </p>
              )}
            </div>
          </Section>

          <Section title="Implementation">
            {!hasCodeBase && !rec.code && (
              <div className="mb-4 rounded-md border border-iris/30 bg-iris/5 p-3 text-sm">
                <p className="font-semibold text-bone">Get an exact code diff for this fix</p>
                <p className="mt-1 text-xs text-muted">
                  No code base is connected to this domain, so guidance stays prose-only. Connect
                  your repository and Lizrd grounds each fix in your actual code — a copy-paste
                  diff, the right file, verified against your IaC.
                </p>
                <Link
                  to={`/domains/${domainId}/integrations`}
                  className="mt-2 inline-block text-xs font-semibold text-iris hover:text-iris-strong"
                >
                  Connect a code base →
                </Link>
              </div>
            )}
            {g ? (
              <div className="space-y-4">
                <Field label="What the change is">
                  <InlineMarkdown text={g.actionExplanation} />
                </Field>
                <div>
                  <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                    Steps
                  </div>
                  <ol className="mt-1 list-decimal space-y-1 pl-5 text-sm text-haze">
                    {g.applySteps.map((s, i) => (
                      <li key={i}>
                        <InlineMarkdown text={s} />
                      </li>
                    ))}
                  </ol>
                </div>
                {g.diff && (
                  <div>
                    <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                      Diff
                    </div>
                    <DiffBlock diff={g.diff} />
                  </div>
                )}
                {g.watchFor.length > 0 && (
                  <div>
                    <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                      What to watch for
                    </div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-haze">
                      {g.watchFor.map((w, i) => (
                        <li key={i}>
                          <InlineMarkdown text={w} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {g.rollback.length > 0 && (
                  <div>
                    <div className="text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                      How to roll it back
                    </div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-haze">
                      {g.rollback.map((r, i) => (
                        <li key={i}>
                          <InlineMarkdown text={r} />
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <RegenerateFooter
                  generatedAt={g.generatedAt}
                  canManage={canManage && !!ai.data?.enabled}
                  regenerating={generating}
                  onRegenerate={() => generate.mutate(undefined)}
                />
              </div>
            ) : generating ? (
              <div className="py-4">
                <StepProgress
                  steps={GUIDANCE_STAGES.map((label, i) => ({
                    label,
                    state: (i < phase ? 'done' : i === phase ? 'active' : 'pending') as StepState,
                  }))}
                />
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-muted">
                  Implementation guidance is generated on demand — the exact steps, a diff to apply,
                  what to watch for, and how to roll it back.
                </p>
                <Button
                  onClick={() => generate.mutate(undefined)}
                  disabled={!ai.data?.enabled || generating}
                >
                  {ai.data?.enabled ? '✦ Generate implementation guidance' : 'Guidance unavailable'}
                </Button>
              </div>
            )}
          </Section>

          {showValidation && (
            <Section title="Validation">
              <div className="space-y-3 text-sm">
                {rec.status === 'validated' ? (
                  <>
                    <p className="text-haze">
                      Proven saving of{' '}
                      <span className="font-semibold text-success">
                        {usd(rec.realizedMonthlySaving ?? 0)}/mo
                      </span>{' '}
                      against an estimate of {usd(rec.estimatedMonthlySaving)}
                      {rec.validationOutcome === 'partial' && ' (partial)'}. Validated{' '}
                      {relativeTime(rec.validatedAt)}.
                    </p>
                    {rec.validationAttribution === 'self_resolved' && (
                      <p className="rounded-md border border-hairline bg-surface-2 px-3 py-2 text-xs text-haze">
                        <span className="font-semibold text-bone">Resolved outside Lizrd.</span>{' '}
                        This wasn&apos;t marked done in Lizrd — we detected the opportunity was gone
                        and credited the realized saving from your latest sync.
                      </p>
                    )}
                    {rec.validationEvidence && (
                      <p className="text-xs text-muted">{rec.validationEvidence}</p>
                    )}
                  </>
                ) : rec.status === 'failed_validation' ? (
                  <p className="text-danger">
                    Validation didn&apos;t confirm the saving.{' '}
                    {rec.validationEvidence && (
                      <span className="text-haze">{rec.validationEvidence}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-muted">
                    Executed {relativeTime(rec.executedAt)} — awaiting validation. We re-check the
                    resource each sync and confirm the realized saving automatically.
                  </p>
                )}
              </div>
            </Section>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <Section title="Involved assets">
            <div className="space-y-3 text-sm">
              <div className="rounded-md border border-hairline bg-surface-2 px-3 py-2">
                <div className="flex items-center gap-2">
                  <NodeTypeIcon type={rec.resourceType} className="h-4 w-4 text-muted" />
                  <span className="truncate font-medium text-bone">{rec.resourceName}</span>
                </div>
                <div className="text-[0.7rem] text-muted">
                  {typeLabel(rec.resourceType, rec.provider)} · {rec.provider}
                </div>
              </div>
              {rec.code && (
                <div className="rounded-md border border-hairline bg-surface-2 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <FileText aria-hidden className="h-4 w-4 shrink-0 text-muted" />
                    <span className="truncate text-bone">{rec.code.file ?? rec.code.repo}</span>
                    {rec.code.verifiedFromState && (
                      <Badge variant="success">verified from state</Badge>
                    )}
                  </div>
                  <div className="truncate text-[0.7rem] text-muted">{rec.code.repo}</div>
                </div>
              )}
              {(neighbors.data?.neighbors.length ?? 0) > 0 && (
                <div>
                  <div className="mb-1 text-[0.7rem] font-semibold uppercase tracking-wide text-muted">
                    Related resources
                  </div>
                  <ul className="space-y-1">
                    {neighbors.data!.neighbors.slice(0, 6).map((n) => (
                      <li key={n.id} className="flex items-center gap-2 truncate text-xs text-haze">
                        <NodeTypeIcon type={n.type} className="h-4 w-4 shrink-0 text-muted" />
                        <span className="truncate">{n.name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Link
                to={`/domains/${domainId}/explorer`}
                className="inline-block text-xs font-semibold text-iris hover:text-iris-strong"
              >
                Open in Explorer →
              </Link>
            </div>
          </Section>

          <Section title="Details">
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted">Assignee</dt>
                <dd>
                  <AssigneeControl rec={rec} envId={domainId} canManage={canManage} />
                </dd>
              </div>
              <Row label="First seen" value={relativeTime(rec.firstSeenAt)} />
              {g && <Row label="Risk" value={g.riskLevel} />}
              {rec.executedByName && <Row label="Executed by" value={rec.executedByName} />}
            </dl>
          </Section>

          <Section title="Activity">
            {history.isLoading ? (
              <SkeletonText lines={3} lineClassName="h-4" />
            ) : (history.data?.events.length ?? 0) === 0 ? (
              <p className="text-xs text-muted">No activity yet.</p>
            ) : (
              <ol className="space-y-2">
                {history.data!.events.map((e) => (
                  <li key={e.id} className="border-l border-hairline pl-3 text-xs">
                    <div className="text-bone">
                      {e.detail ?? e.type}
                      {e.actorName && <span className="text-muted"> · {e.actorName}</span>}
                    </div>
                    <div className="text-[0.65rem] text-muted">{relativeTime(e.createdAt)}</div>
                  </li>
                ))}
              </ol>
            )}
          </Section>

          <Section title="Comments">
            <div className="space-y-3">
              {(comments.data?.comments.length ?? 0) > 0 && (
                <ul className="space-y-2">
                  {comments.data!.comments.map((c) => (
                    <li
                      key={c.id}
                      className="rounded-md border border-hairline bg-surface-2 px-3 py-2 text-sm"
                    >
                      <div className="text-[0.65rem] text-muted">
                        {c.authorName} · {relativeTime(c.createdAt)}
                      </div>
                      <div className="mt-0.5 text-haze">{c.body}</div>
                    </li>
                  ))}
                </ul>
              )}
              <form
                onSubmit={(e: FormEvent) => {
                  e.preventDefault();
                  if (comment.trim()) postComment.mutate();
                }}
                className="space-y-2"
              >
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment…"
                  rows={2}
                  className="w-full resize-none rounded-md border border-border bg-surface-2 px-3 py-2 text-sm text-bone placeholder:text-muted focus:border-iris focus:outline-none"
                />
                <Button
                  type="submit"
                  size="sm"
                  variant="outline"
                  disabled={!comment.trim() || postComment.isPending}
                >
                  {postComment.isPending ? <Spinner size="sm" /> : 'Comment'}
                </Button>
              </form>
            </div>
          </Section>
        </div>
      </div>

      <RejectRecommendationModal
        open={rejecting}
        title={rec.title}
        pending={act.isPending}
        onCancel={() => setRejecting(false)}
        onConfirm={(reason, note) => {
          act.mutate({
            action: 'reject',
            rejectionReason: reason,
            rejectionNote: note || undefined,
          });
          setRejecting(false);
        }}
      />
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="truncate capitalize text-bone">{value}</dd>
    </div>
  );
}
